export const convertToEnglishWords = (num: number): string => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const helper = (n: number): string => {
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "");
        if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 !== 0 ? " and " + helper(n % 100) : "");
        if (n < 100000) return helper(Math.floor(n / 1000)) + " Thousand" + (n % 1000 !== 0 ? " " + helper(n % 1000) : "");
        if (n < 10000000) return helper(Math.floor(n / 100000)) + " Lakh" + (n % 100000 !== 0 ? " " + helper(n % 100000) : "");
        return helper(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 !== 0 ? " " + helper(n % 10000000) : "");
    };

    return helper(num).trim();
};

export const convertToSinhalaWords = (num: number): string => {
    const ones = ["", "එක", "දෙක", "තුන", "හතර", "පහ", "හය", "හත", "අට", "නමය"];
    const teens = ["දහය", "එකොළහ", "දොළහ", "දහතුන", "දහහතර", "පහළොව", "දහසය", "දහහත", "දහඅට", "දහනවය"];
    const tens = ["", "", "විස්ස", "තිහ", "හතළිහ", "පනහ", "හැට", "හැත්තෑව", "අසූව", "අනූව"];
    const tenPrefixes = ["", "", "විසි", "තිස්", "හතළිස්", "පනස්", "හැට", "හැත්තෑ", "අසූ", "අනූ"];
    const hundreds = ["", "සියය", "දෙසියය", "තුන්සියය", "හාරසියය", "පන්සියය", "හයසියය", "හත්සියය", "අටසියය", "නමසියය"];
    const hundredPrefixes = ["", "එකසිය", "දෙසිය", "තුන්සිය", "හාරසිය", "පන්සිය", "හයසිය", "හත්සිය", "අටසිය", "නමසිය"];

    const getThousandWord = (th: number): string => {
        const onesPrefix = ["", "එක්", "දෙ", "තුන්", "හතර", "පන්", "හය", "හත්", "අට", "නම"];
        const baseTh = ["", "එක්දහස", "දෙදහස", "තුන්දහස", "හාරදහස", "පන්දහස", "හයදහස", "හත්දහස", "අටදහස", "නමදහස"];
        const teensPrefix = [
            "දස", // 10
            "එකොළොස්", // 11
            "දොළොස්", // 12
            "දහතුන්", // 13
            "දහහතර", // 14
            "පහළොස්", // 15
            "දහසය", // 16
            "දහහත්", // 17
            "දහඅට", // 18
            "දහනව" // 19
        ];
        const tensPrefix = ["", "", "විසි", "තිස්", "හතළිස්", "පනස්", "හැට", "හැත්තෑ", "අසූ", "අනූ"];

        if (th < 10) {
            return baseTh[th];
        }
        if (th < 20) {
            return teensPrefix[th - 10] + "දහස";
        }
        const t = Math.floor(th / 10);
        const r = th % 10;
        if (r === 0) {
            return tensPrefix[t] + "දහස";
        }
        return tensPrefix[t] + onesPrefix[r] + "දහස";
    };

    const getLakhWord = (l: number, isPrefix: boolean): string => {
        const onesPrefix = ["", "එක", "දෙ", "තුන්", "හතර", "පන්", "හය", "හත්", "අට", "නම"];
        const lakhWords = ["", "ලක්ෂය", "දෙලක්ෂය", "තුන්ලක්ෂය", "හාරලක්ෂය", "පන්ලක්ෂය", "හයලක්ෂය", "හත්ලක්ෂය", "අටලක්ෂය", "නමලක්ෂය"];
        const lakhPrefixes = ["", "එක්ලක්ෂ", "දෙලක්ෂ", "තුන්ලක්ෂ", "හාරලක්ෂ", "පන්ලක්ෂ", "හයලක්ෂ", "හත්ලක්ෂ", "අටලක්ෂ", "නමලක්ෂ"];
        const teensPrefix = ["දස", "එකොළොස්", "දොළොස්", "දහතුන්", "දහහතර", "පහළොස්", "දහසය", "දහහත්", "දහඅට", "දහනව"];
        const tensPrefix = ["", "", "විසි", "තිස්", "හතළිස්", "පනස්", "හැට", "හැත්තෑ", "අසූ", "අනූ"];

        if (l < 10) {
            return isPrefix ? lakhPrefixes[l] : lakhWords[l];
        }
        if (l < 20) {
            const suffix = isPrefix ? "ලක්ෂ" : "ලක්ෂය";
            return teensPrefix[l - 10] + " " + suffix;
        }
        const t = Math.floor(l / 10);
        const r = l % 10;
        const suffix = isPrefix ? "ලක්ෂ" : "ලක්ෂය";
        if (r === 0) {
            return tensPrefix[t] + " " + suffix;
        }
        return tensPrefix[t] + " " + onesPrefix[r] + " " + suffix;
    };

    const getCroreWord = (c: number, isPrefix: boolean): string => {
        const onesPrefix = ["", "එක්", "දෙ", "තුන්", "හතර", "පන්", "හය", "හත්", "අට", "නම"];
        const kotiWords = ["", "එක්කෝටිය", "දෙකෝටිය", "තුන්කෝටිය", "හාරකෝටිය", "පන්කෝටිය", "හයකෝටිය", "හත්කෝටිය", "අටකෝටිය", "නමකෝටිය"];
        const kotiPrefixes = ["", "එක්කෝටි", "දෙකෝටි", "තුන්කෝටි", "හාරකෝටි", "පන්කෝටි", "හයකෝටි", "හත්කෝටි", "අටකෝටි", "නමකෝටි"];
        const teensPrefix = ["දස", "එකොළොස්", "දොළොස්", "දහතුන්", "දහහතර", "පහළොස්", "දහසය", "දහහත්", "දහඅට", "දහනව"];
        const tensPrefix = ["", "", "විසි", "තිස්", "හතළිස්", "පනස්", "හැට", "හැත්තෑ", "අසූ", "අනූ"];

        if (c < 10) {
            return isPrefix ? kotiPrefixes[c] : kotiWords[c];
        }
        if (c < 20) {
            const suffix = isPrefix ? "කෝටි" : "කෝටිය";
            return teensPrefix[c - 10] + suffix;
        }
        const t = Math.floor(c / 10);
        const r = c % 10;
        const suffix = isPrefix ? "කෝටි" : "කෝටිය";
        if (r === 0) {
            return tensPrefix[t] + suffix;
        }
        return tensPrefix[t] + onesPrefix[r] + suffix;
    };

    const helper = (n: number): string => {
        if (n === 0) return "";
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) {
            const t = Math.floor(n / 10);
            const r = n % 10;
            return r === 0 ? tens[t] : tenPrefixes[t] + " " + helper(r);
        }
        if (n < 1000) {
            const h = Math.floor(n / 100);
            const r = n % 100;
            return r === 0 ? hundreds[h] : hundredPrefixes[h] + " " + helper(r);
        }
        if (n < 100000) {
            const th = Math.floor(n / 1000);
            const r = n % 1000;
            if (r === 0) {
                if (th === 1) return "දහස";
                return getThousandWord(th);
            }
            return getThousandWord(th).replace("දහස", "දහස්") + " " + helper(r);
        }
        if (n < 10000000) {
            const l = Math.floor(n / 100000);
            const r = n % 100000;
            if (l < 100) {
                return r === 0 ? getLakhWord(l, false) : getLakhWord(l, true) + " " + helper(r);
            }
            const lWord = helper(l) + " ලක්ෂය";
            const lPrefix = helper(l) + " ලක්ෂ";
            return r === 0 ? lWord : lPrefix + " " + helper(r);
        }
        const c = Math.floor(n / 10000000);
        const r = n % 10000000;
        if (c < 100) {
            return r === 0 ? getCroreWord(c, false) : getCroreWord(c, true) + " " + helper(r);
        }
        const cWord = helper(c) + " කෝටිය";
        const cPrefix = helper(c) + " කෝටි";
        return r === 0 ? cWord : cPrefix + " " + helper(r);
    };

    const raw = helper(num).trim();
    if (!raw) return "";

    if (raw.endsWith("ය")) {
        return raw.substring(0, raw.length - 1) + "යක්";
    }
    const lastChar = raw.charAt(raw.length - 1);
    if (["ක", "න", "ර", "හ", "ට", "ත", "ම", "ස", "ව"].includes(lastChar)) {
        return raw + "ක්";
    }
    return raw;
};

export const convertToTamilWords = (num: number): string => {
    const ones = ["", "ஒன்று", "இரண்டு", "மூன்று", "நான்கு", "ஐந்து", "ஆறு", "ஏழு", "எட்டு", "ஒன்பது"];
    const teens = ["பத்து", "பதினொன்று", "பன்னிரண்டு", "பதிமூன்று", "பதினான்கு", "பதினைந்து", "பதினாறு", "பதினேழு", "பதினெட்டு", "பத்தொன்பது"];
    const tens = ["", "", "இருபது", "முப்பது", "நாற்பது", "ஐம்பது", "அறுபது", "எழுபது", "எண்பது", "தொண்ணூறு"];
    const tenPrefixes = ["", "", "இருபத்து", "முப்பத்து", "நாற்பத்து", "ஐம்பத்து", "அறுபத்து", "எழுபத்து", "எண்பத்து", "தொண்ணூற்று"];
    const hundreds = ["", "நூறு", "இருநூறு", "முந்நூறு", "நானூறு", "ஐந்நூறு", "அறுநூறு", "எழுநூறு", "எண்ணூறு", "தொள்ளாயிரம்"];
    const hundredPrefixes = ["", "நூற்று", "இருநூற்று", "முந்நூற்று", "நானூற்று", "ஐந்நூற்று", "அறுநூற்று", "எழுநூற்று", "எண்ணூற்று", "தொள்ளாயிரத்து"];

    const helper = (n: number): string => {
        if (n === 0) return "";
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) {
            const t = Math.floor(n / 10);
            const r = n % 10;
            return r === 0 ? tens[t] : tenPrefixes[t] + " " + helper(r);
        }
        if (n < 1000) {
            const h = Math.floor(n / 100);
            const r = n % 100;
            return r === 0 ? hundreds[h] : hundredPrefixes[h] + " " + helper(r);
        }
        if (n < 100000) {
            const th = Math.floor(n / 1000);
            const r = n % 1000;
            const thWord = th === 1 ? "ஆயிரம்" : helper(th) + "ாயிரம்";
            const thPrefix = th === 1 ? "ஆயிரத்து" : helper(th) + "ாயிரத்து";
            return r === 0 ? thWord : thPrefix + " " + helper(r);
        }
        if (n < 10000000) {
            const l = Math.floor(n / 100000);
            const r = n % 100000;
            const lWord = l === 1 ? "இலட்சம்" : helper(l) + " இலட்சம்";
            const lPrefix = l === 1 ? "இலட்சத்து" : helper(l) + " இலட்சத்து";
            return r === 0 ? lWord : lPrefix + " " + helper(r);
        }
        const c = Math.floor(n / 10000000);
        const r = n % 10000000;
        const cWord = helper(c) + " கோடி";
        const cPrefix = helper(c) + " கோடியே";
        return r === 0 ? cWord : cPrefix + " " + helper(r);
    };

    return helper(num).trim();
};

export const numberToWords = (num: number, lang: string): string => {
    if (num === 0) {
        if (lang.startsWith("si")) return "රුපියල් බිංදුවක් පමණි";
        if (lang.startsWith("ta")) return "பூஜ்யம் ரூபாய் மட்டும்";
        return "Zero Rupees Only";
    }

    const parts = num.toFixed(2).split(".");
    const rupees = parseInt(parts[0], 10);
    const cents = parseInt(parts[1], 10);

    let result = "";

    if (lang.startsWith("si")) {
        const rText = convertToSinhalaWords(rupees);
        const cText = cents > 0 ? convertToSinhalaWords(cents) : "";
        if (rupees > 0 && cents > 0) {
            const rFormatted = rText.endsWith("ක්") ? rText.slice(0, -1) + "යි" : rText;
            result = `රුපියල් ${rFormatted} ශත ${cText} පමණි`;
        } else if (rupees > 0) {
            result = `රුපියල් ${rText} පමණි`;
        } else if (cents > 0) {
            result = `ශත ${cText} පමණි`;
        }
    } else if (lang.startsWith("ta")) {
        const rText = convertToTamilWords(rupees);
        const cText = cents > 0 ? convertToTamilWords(cents) : "";
        if (rupees > 0 && cents > 0) {
            result = `${rText} ரூபாய் ${cText} சதம் மட்டும்`;
        } else if (rupees > 0) {
            result = `${rText} ரூபாய் மட்டும்`;
        } else if (cents > 0) {
            result = `${cText} சதம் மட்டும்`;
        }
    } else {
        const rText = convertToEnglishWords(rupees);
        const cText = cents > 0 ? convertToEnglishWords(cents) : "";
        if (rupees > 0 && cents > 0) {
            result = `${rText} Rupees and ${cText} Cents Only`;
        } else if (rupees > 0) {
            result = `${rText} Rupees Only`;
        } else if (cents > 0) {
            result = `${cText} Cents Only`;
        }
    }

    return result;
};
